import { io, Socket } from 'socket.io-client'
import { Issue } from '@/types'

class SocketService {
  private socket: Socket | null = null
  private currentProjectId: string | null = null

  // 初始化 Socket 連接
  connect() {
    if (this.socket?.connected) {
      return this.socket
    }

    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.warn('No auth token found, skipping socket connection')
      return null
    }

    this.socket = io('/', {
      auth: {
        token
      },
      transports: ['websocket', 'polling'],
      path: '/socket.io/'
    })

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id)
    })

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected')
    })

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error)
    })

    return this.socket
  }

  // 加入專案房間
  joinProject(projectId: string) {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot join project')
      return
    }

    // 如果已經在其他專案房間，先離開
    if (this.currentProjectId && this.currentProjectId !== projectId) {
      this.leaveProject(this.currentProjectId)
    }

    this.socket.emit('join-project', projectId)
    this.currentProjectId = projectId
    console.log(`Joined project room: ${projectId}`)
  }

  // 離開專案房間
  leaveProject(projectId?: string) {
    if (!this.socket?.connected) {
      return
    }

    const targetProjectId = projectId || this.currentProjectId
    if (targetProjectId) {
      this.socket.emit('leave-project', targetProjectId)
      console.log(`Left project room: ${targetProjectId}`)
      
      if (targetProjectId === this.currentProjectId) {
        this.currentProjectId = null
      }
    }
  }

  // 監聽議題建立事件
  onIssueCreated(callback: (data: { issue: Issue; project: { id: string } }) => void) {
    if (!this.socket) {
      console.warn('Socket not connected, cannot listen to issue creation')
      return
    }

    this.socket.on('issueCreated', callback)
  }

  // 移除議題建立事件監聽
  offIssueCreated() {
    if (this.socket) {
      this.socket.off('issueCreated')
    }
  }

  // 監聽議題更新事件
  onIssueUpdated(callback: (data: { issue: Issue; changes: string[] }) => void) {
    if (!this.socket) {
      console.warn('Socket not connected, cannot listen to issue updates')
      return
    }

    this.socket.on('issueUpdated', callback)
  }

  // 移除議題更新事件監聽
  offIssueUpdated() {
    if (this.socket) {
      this.socket.off('issueUpdated')
    }
  }

  // 監聽議題刪除事件
  onIssueDeleted(callback: (data: { issueId: string; projectId: string }) => void) {
    if (!this.socket) {
      console.warn('Socket not connected, cannot listen to issue deletion')
      return
    }

    this.socket.on('issueDeleted', callback)
  }

  // 移除議題刪除事件監聽
  offIssueDeleted() {
    if (this.socket) {
      this.socket.off('issueDeleted')
    }
  }

  // 監聽議題移動事件
  onIssueMoved(callback: (data: { issueId: string; fromProject: string; toProject: string; userId: string }) => void) {
    if (!this.socket) {
      console.warn('Socket not connected, cannot listen to issue moves')
      return
    }

    this.socket.on('issueMoved', callback)
  }

  // 移除議題移動事件監聽
  offIssueMoved() {
    if (this.socket) {
      this.socket.off('issueMoved')
    }
  }

  // 監聽議題接收事件
  onIssueReceived(callback: (data: { issue: Issue; fromProject: string; userId: string }) => void) {
    if (!this.socket) {
      console.warn('Socket not connected, cannot listen to issue reception')
      return
    }

    this.socket.on('issueReceived', callback)
  }

  // 移除議題接收事件監聽
  offIssueReceived() {
    if (this.socket) {
      this.socket.off('issueReceived')
    }
  }

  onNotification(callback: (payload: any) => void) {
    if (!this.socket) {
      console.warn('Socket not connected, cannot listen to notifications')
      return
    }

    this.socket.on('notification', callback)
  }

  offNotification(callback: (payload: any) => void) {
    if (this.socket) {
      this.socket.off('notification', callback)
    }
  }

  // 斷開連接
  disconnect() {
    if (this.socket) {
      if (this.currentProjectId) {
        this.leaveProject(this.currentProjectId)
      }
      this.socket.disconnect()
      this.socket = null
      this.currentProjectId = null
      console.log('Socket disconnected manually')
    }
  }

  // 獲取連接狀態
  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  // 獲取當前專案 ID
  getCurrentProjectId(): string | null {
    return this.currentProjectId
  }
}

// 創建單例實例
export const socketService = new SocketService()
export default socketService
